import React from 'react';
import { Button } from '@/components/ui/button';
import { Home, MapPin, Pencil, Navigation } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StartLocationSelectorProps {
  mode: 'default' | 'custom';
  onModeChange: (mode: 'default' | 'custom') => void;
  defaultLocationName?: string;
  customLocationName?: string;
  onEditCustom: () => void;
}

/**
 * Compact toggle for selecting route starting location
 * Displays segmented button (Default/Custom) with current address
 */
export const StartLocationSelector: React.FC<StartLocationSelectorProps> = ({
  mode,
  onModeChange,
  defaultLocationName = 'Default Location',
  customLocationName,
  onEditCustom
}) => {
  const currentAddress =
    mode === 'custom' && customLocationName ? customLocationName : defaultLocationName;

  return (
    <div className='flex items-center gap-3 min-w-0'>
      {/* Toggle */}
      <div className='flex items-center gap-0.5 bg-muted rounded-lg p-0.5 flex-shrink-0'>
        <Button
          variant={mode === 'default' ? 'default' : 'ghost'}
          size='sm'
          className={cn(
            'h-6 px-2 text-xs rounded-md transition-all',
            mode === 'default'
              ? 'bg-primary text-primary-foreground shadow-sm font-medium'
              : 'text-muted-foreground hover:text-foreground'
          )}
          onClick={() => onModeChange('default')}
        >
          <Home className='h-3 w-3 mr-1' />
          Default
        </Button>
        <Button
          variant={mode === 'custom' ? 'default' : 'ghost'}
          size='sm'
          className={cn(
            'h-6 px-2 text-xs rounded-md transition-all',
            mode === 'custom'
              ? 'bg-primary text-primary-foreground shadow-sm font-medium'
              : 'text-muted-foreground hover:text-foreground'
          )}
          onClick={() => onModeChange('custom')}
        >
          <MapPin className='h-3 w-3 mr-1' />
          Custom
        </Button>
      </div>

      {/* Address Display - inline, truncated */}
      <div className='flex items-center gap-1.5 min-w-0 flex-1'>
        <Navigation className='h-3.5 w-3.5 text-primary flex-shrink-0' />
        <span
          className='text-xs text-foreground font-medium truncate'
          title={currentAddress || 'No location set'}
        >
          {currentAddress || 'No location set'}
        </span>
        {mode === 'custom' && customLocationName && (
          <Button
            variant='ghost'
            size='sm'
            className='h-5 w-5 p-0 flex-shrink-0'
            onClick={onEditCustom}
            title='Edit custom location'
          >
            <Pencil className='h-3 w-3' />
          </Button>
        )}
      </div>
    </div>
  );
};

export default StartLocationSelector;
